"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { usePathname, useRouter } from "next/navigation";
import { MOUVEMENTS_INIT, PRODUITS_INIT, RAPPROCHEMENTS_INIT } from "@/lib/data";
import { chargerPennylane, lireBonDeLivraison, lireEtiquette } from "@/lib/api";
import { pathForVue, vueFromPath } from "@/lib/routes";
import type {
  Achat,
  Facture,
  FiltreStock,
  InventaireState,
  Mouvement,
  Produit,
  Rapprochement,
  ReceptionState,
  VueId,
} from "@/lib/types";
import { statut } from "@/lib/utils";

type ToastState = { msg: string; key: number };

type StockContextValue = {
  vue: VueId;
  go: (vue: VueId) => void;
  produits: Produit[];
  mouvements: Mouvement[];
  factures: Facture[];
  achats: Achat[];
  ocrAchat: (id: string) => Promise<{ enStock?: boolean; lignes?: number } | void>;
  rapprochements: Rapprochement[];
  filtre: FiltreStock;
  setFiltre: (f: FiltreStock) => void;
  allee: string;
  setAllee: (a: string) => void;
  recherche: string;
  setRecherche: (q: string) => void;
  syncMin: number;
  syncing: boolean;
  sync: (refresh?: boolean) => Promise<void>;
  reception: ReceptionState;
  setReception: (r: ReceptionState | ((prev: ReceptionState) => ReceptionState)) => void;
  inventaire: InventaireState;
  setInventaire: (i: InventaireState | ((prev: InventaireState) => InventaireState)) => void;
  panelRef: string | null;
  ouvrirPanel: (ref: string) => void;
  fermerPanel: () => void;
  toast: ToastState | null;
  showToast: (msg: string) => void;
  bouger: (ref: string, delta: number, type: Mouvement["type"], source: string) => void;
  findProduit: (ref: string) => Produit | undefined;
  lignesAAssocier: () => (Facture["lignes"][number] & { num: string; canal: string })[];
  nbEcarts: () => number;
  badge: (id: VueId) => number;
  corrigerStock: (delta: number, motif: string) => void;
  /** Pose le stock à une quantité comptée (inventaire manuel). */
  saisirInventaire: (ref: string, qteComptee: number) => void;
  lancerLecture: (photoUrl: string | null) => Promise<void>;
  validerReception: () => void;
  resetReception: () => void;
  associerLigne: (id: string, ref: string, reten: boolean) => void;
  ignorerLigne: (id: string) => void;
  verifierFacture: (id: string) => void;
  lireEtiquetteAllee: () => Promise<void>;
  validerInventaire: () => void;
};

const StockContext = createContext<StockContextValue | null>(null);

export function StockProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const vue = vueFromPath(pathname);
  const [produits, setProduits] = useState<Produit[]>(() => PRODUITS_INIT.map((p) => ({ ...p })));
  const [mouvements, setMouvements] = useState<Mouvement[]>(() => [...MOUVEMENTS_INIT]);
  const [factures, setFactures] = useState<Facture[]>([]);
  const [achats, setAchats] = useState<Achat[]>([]);
  const [rapprochements, setRapprochements] = useState<Rapprochement[]>(() =>
    RAPPROCHEMENTS_INIT.map((r) => ({ ...r, lignes: r.lignes.map((l) => ({ ...l })) })),
  );
  const [filtre, setFiltre] = useState<FiltreStock>("tous");
  const [allee, setAllee] = useState("toutes");
  const [recherche, setRecherche] = useState("");
  const [syncMin, setSyncMin] = useState(-1);
  const [syncing, setSyncing] = useState(false);
  const [reception, setReception] = useState<ReceptionState>({
    etape: 1,
    fournisseur: "Grossiste A",
    photo: null,
    lignes: [],
  });
  const [inventaire, setInventaire] = useState<InventaireState>({ allee: "A", comptes: {} });
  const [panelRef, setPanelRef] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [correspondances, setCorrespondances] = useState<Record<string, string>>({});

  const showToast = useCallback((msg: string) => {
    setToast({ msg, key: Date.now() });
  }, []);

  const findProduit = useCallback((ref: string) => produits.find((p) => p.ref === ref), [produits]);

  const bouger = useCallback((ref: string, delta: number, type: Mouvement["type"], source: string) => {
    if (!delta) return;
    setProduits((prev) =>
      prev.map((p) => (p.ref === ref ? { ...p, qte: Math.max(0, p.qte + delta) } : p)),
    );
    setMouvements((prev) => [{ quand: "À l'instant", type, ref, delta, source }, ...prev]);
  }, []);

  const go = useCallback(
    (v: VueId) => {
      setPanelRef(null);
      window.scrollTo({ top: 0 });
      const next = pathForVue(v);
      if (next !== pathname) router.push(next);
    },
    [pathname, router],
  );

  const ouvrirPanel = useCallback((ref: string) => setPanelRef(ref), []);
  const fermerPanel = useCallback(() => setPanelRef(null), []);

  const lignesAAssocier = useCallback(
    () =>
      factures.flatMap((f) =>
        f.lignes
          .filter((l) => l.statut === "associer")
          .map((l) => ({ ...l, num: f.num, canal: f.canal })),
      ),
    [factures],
  );

  const ecartsDe = useCallback(
    (r: Rapprochement) => r.lignes.filter((l) => l.recu !== l.facture || l.puCmd !== l.puFact),
    [],
  );

  const nbEcarts = useCallback(
    () => rapprochements.filter((r) => !r.verifie && ecartsDe(r).length).length,
    [rapprochements, ecartsDe],
  );

  const badge = useCallback(
    (id: VueId) => {
      if (id === "ventes") return lignesAAssocier().length;
      if (id === "fournisseurs") return nbEcarts();
      return 0;
    },
    [lignesAAssocier, nbEcarts],
  );

  const ocrAchat = useCallback(
    async (id: string) => {
      const res = await fetch("/api/pennylane/achats/ocr", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      const data = (await res.json()) as {
        achat?: Achat;
        error?: string;
        lignes?: number;
        enStock?: boolean;
        source?: "xml" | "ocr";
      };
      if (!res.ok) throw new Error(data.error || "OCR impossible");
      if (data.achat) {
        setAchats((prev) => prev.map((a) => (a.id === id ? { ...data.achat!, ocrFait: true } : a)));
        const n = data.lignes ?? data.achat.lignes.length;
        showToast(
          data.source === "xml" ||
            data.achat.lignes?.some((l) => String(l.id).startsWith("xml-"))
            ? `${n} lignes lues depuis le XML e-invoice`
            : `${n} lignes lues dans le PDF (stock à marquer à la main)`,
        );
      }
      return data;
    },
    [showToast],
  );

  const sync = useCallback(async (refresh = true) => {
    setSyncing(true);
    try {
      if (refresh) showToast("Import Pennylane depuis février…");
      const data = await chargerPennylane(refresh);
      setFactures(data.factures);
      setAchats(data.achats);
      setSyncMin(0);
      showToast(
        `${data.factures.length} ventes · ${data.achats.length} achats (depuis février)`,
      );
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Lecture Pennylane impossible");
    } finally {
      setSyncing(false);
    }
  }, [showToast]);

  const corrigerStock = useCallback(
    (delta: number, motif: string) => {
      if (!panelRef || !delta) return;
      bouger(panelRef, delta, "correction", motif);
      showToast(`Stock corrigé : ${delta > 0 ? "+" : ""}${delta}`);
    },
    [panelRef, bouger, showToast],
  );

  const saisirInventaire = useCallback(
    (ref: string, qteComptee: number) => {
      const p = produits.find((x) => x.ref === ref);
      if (!p) {
        showToast("Article introuvable");
        return;
      }
      if (!Number.isFinite(qteComptee) || qteComptee < 0) {
        showToast("Indique une quantité ≥ 0");
        return;
      }
      const cible = Math.round(qteComptee);
      const delta = cible - p.qte;
      if (!delta) {
        showToast(`${p.nom} : déjà à ${cible}`);
        return;
      }
      bouger(ref, delta, "inventaire", "Inventaire manuel");
      showToast(
        `${p.nom} : ${p.qte} → ${cible} (${delta > 0 ? "+" : ""}${delta})`,
      );
    },
    [produits, bouger, showToast],
  );

  const lancerLecture = useCallback(
    async (photoUrl: string | null) => {
      setReception((r) => ({ ...r, photo: photoUrl, etape: "lecture" }));
      const lignes = await lireBonDeLivraison(photoUrl);
      setReception((r) => (r.etape === "lecture" ? { ...r, lignes, etape: 2 } : r));
    },
    [],
  );

  const resetReception = useCallback(() => {
    setReception((r) => ({ etape: 1, fournisseur: r.fournisseur, photo: null, lignes: [] }));
  }, []);

  const validerReception = useCallback(() => {
    let lignes = 0;
    let unites = 0;
    let ignorees = 0;
    reception.lignes.forEach((l) => {
      if (!l.ref || l.ref === "__new" || !l.qte) {
        ignorees++;
        return;
      }
      bouger(l.ref, l.qte, "reception", `BL ${reception.fournisseur}`);
      lignes++;
      unites += l.qte;
    });
    setReception((r) => ({ ...r, resume: { lignes, unites, ignorees }, etape: 3 }));
    showToast(`${unites} unités entrées en stock`);
  }, [reception, bouger, showToast]);

  const associerLigne = useCallback(
    (id: string, ref: string, reten: boolean) => {
      const f = factures.find((x) => x.lignes.some((l) => l.id === id));
      const l = f?.lignes.find((x) => x.id === id);
      if (!f || !l) return;
      if (reten) setCorrespondances((c) => ({ ...c, [l.texte.toLowerCase()]: ref }));
      setFactures((prev) =>
        prev.map((fx) =>
          fx.num !== f.num
            ? fx
            : {
                ...fx,
                lignes: fx.lignes.map((lx) =>
                  lx.id === id ? { ...lx, ref, statut: "ok" as const } : lx,
                ),
              },
        ),
      );
      bouger(ref, -l.qte, "vente", `${f.num} · ${f.canal}`);
      const p = produits.find((x) => x.ref === ref);
      showToast(`Associé à ${p?.nom ?? ref} · -${l.qte} en stock`);
      void correspondances;
    },
    [factures, produits, bouger, showToast, correspondances],
  );

  const ignorerLigne = useCallback(
    (id: string) => {
      setFactures((prev) =>
        prev.map((f) => ({
          ...f,
          lignes: f.lignes.map((l) => (l.id === id ? { ...l, statut: "ignore" as const } : l)),
        })),
      );
      showToast("Ligne ignorée (service, port, main d'œuvre…)");
    },
    [showToast],
  );

  useEffect(() => {
    void sync(false);
  }, [sync]);

  const verifierFacture = useCallback(
    (id: string) => {
      setRapprochements((prev) => prev.map((r) => (r.id === id ? { ...r, verifie: true } : r)));
      showToast("Facture marquée comme vérifiée");
    },
    [showToast],
  );

  const lireEtiquetteAllee = useCallback(async () => {
    const rows = produits.filter(
      (p) => p.emp.startsWith(inventaire.allee) && inventaire.comptes[p.ref] === undefined,
    );
    if (!rows.length) {
      showToast("Tous les articles de l'allée sont comptés");
      return;
    }
    showToast("Lecture de l'étiquette…");
    const res = await lireEtiquette(rows[0].ref, produits);
    setInventaire((inv) => ({ ...inv, comptes: { ...inv.comptes, [res.ref]: res.qte } }));
    showToast(`Étiquette lue : ${res.ref} · ${res.qte} unités`);
  }, [produits, inventaire, showToast]);

  const validerInventaire = useCallback(() => {
    const rows = produits.filter((p) => p.emp.startsWith(inventaire.allee));
    let n = 0;
    let ec = 0;
    const nextComptes = { ...inventaire.comptes };
    rows.forEach((p) => {
      const c = inventaire.comptes[p.ref];
      if (c === undefined) return;
      n++;
      const d = c - p.qte;
      if (d) {
        ec++;
        bouger(p.ref, d, "inventaire", `Recomptage allée ${inventaire.allee}`);
      }
      delete nextComptes[p.ref];
    });
    if (!n) {
      showToast("Saisis au moins une quantité comptée");
      return;
    }
    setInventaire((inv) => ({ ...inv, comptes: nextComptes }));
    showToast(
      `${n} article${n > 1 ? "s" : ""} comptés · ${ec} écart${ec > 1 ? "s" : ""} corrigé${ec > 1 ? "s" : ""}`,
    );
  }, [produits, inventaire, bouger, showToast]);

  const value = useMemo<StockContextValue>(
    () => ({
      vue,
      go,
      produits,
      mouvements,
      factures,
      achats,
      ocrAchat,
      rapprochements,
      filtre,
      setFiltre,
      allee,
      setAllee,
      recherche,
      setRecherche,
      syncMin,
      syncing,
      sync,
      reception,
      setReception,
      inventaire,
      setInventaire,
      panelRef,
      ouvrirPanel,
      fermerPanel,
      toast,
      showToast,
      bouger,
      findProduit,
      lignesAAssocier,
      nbEcarts,
      badge,
      corrigerStock,
      saisirInventaire,
      lancerLecture,
      validerReception,
      resetReception,
      associerLigne,
      ignorerLigne,
      verifierFacture,
      lireEtiquetteAllee,
      validerInventaire,
    }),
    [
      vue,
      go,
      produits,
      mouvements,
      factures,
      achats,
      ocrAchat,
      rapprochements,
      filtre,
      allee,
      recherche,
      syncMin,
      syncing,
      sync,
      reception,
      inventaire,
      panelRef,
      ouvrirPanel,
      fermerPanel,
      toast,
      showToast,
      bouger,
      findProduit,
      lignesAAssocier,
      nbEcarts,
      badge,
      corrigerStock,
      saisirInventaire,
      lancerLecture,
      validerReception,
      resetReception,
      associerLigne,
      ignorerLigne,
      verifierFacture,
      lireEtiquetteAllee,
      validerInventaire,
    ],
  );

  return <StockContext.Provider value={value}>{children}</StockContext.Provider>;
}

export function useStock() {
  const ctx = useContext(StockContext);
  if (!ctx) throw new Error("useStock must be used within StockProvider");
  return ctx;
}

export function useStockStatut(p: Produit) {
  return statut(p);
}
